/**
 * UI State Store
 * Manages global UI state including loading states and toast notifications
 */

import { create } from 'zustand';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
  createdAt?: number;
}

interface UIState {
  loading: boolean;
  toasts: Toast[];

  // Actions
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  removeToast: (id: string) => void;
  setLoading: (loading: boolean) => void;
}

/**
 * UI Store
 * Manages toast notifications and global loading states
 */
export const useUIStore = create<UIState>((set, get) => ({
  loading: false,
  toasts: [],

  /**
   * Show a toast notification
   * Auto-generates unique ID and adds to toast list
   */
  showToast: (message: string, type: 'success' | 'error' | 'info') => {
    // Same message raised again within a moment (e.g. two error paths for one failure): don't stack it
    const now = Date.now();
    if (get().toasts.some((toast) => toast.message === message && now - (toast.createdAt ?? 0) < 1500)) return;
    const id = `toast-${now}-${Math.random()}`;
    set((state) => ({
      toasts: [...state.toasts, { id, message, type, createdAt: now }],
    }));

    // Auto-remove toast after 5 seconds
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((toast) => toast.id !== id),
      }));
    }, 5000);
  },

  /**
   * Manually remove a toast notification
   */
  removeToast: (id: string) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  /**
   * Set global loading state
   */
  setLoading: (loading: boolean) => {
    set({ loading });
  },
}));
